import { type ReactNode, useRef } from "react";
import { useChangesOn } from "../notifications/StepChanges";
import type {
  ClaimLineResponse,
  ClaimResponse,
  ClaimStrategyResponse,
} from "@patent-studio/api-types";
import { FindingList } from "@/components/patterns/FindingList";
import { Button } from "@/components/ui/button";
import { ItemRow } from "@/components/patterns/ItemRow";
import { StatusPill } from "@/components/ui/StatusPill";
import {
  ReferenceWords,
  type StructuredTextHandle,
} from "@/components/ui/StructuredText";
import { cn } from "@/lib/utils";
import { claimLineStyle } from "./claim-line-style";
import { useDrawnElements } from "./drawn-elements";
import { ClaimRefusal, ConfirmToggle, useClaimActs } from "./ClaimEditing";
import { ClaimOptions, RemoveClaim } from "./ClaimOptions";
import { ClaimLines } from "./ClaimLines";
import { OriginPill } from "@/components/patterns/OriginPill";
import { CategoryTag, InheritedTag } from "./CategoryTag";
import { claimAnchor } from "./ClaimStrategyFooter";
import { PositionBasis } from "./PositionBasis";
import { useClaimSetMoments } from "./claim-set-moments";

/** The claim's number in the design's mono chip: an independent claim's in the brand, a dependent's quiet. */
function ClaimNumber({ claim }: { claim: ClaimResponse }) {
  return (
    <span
      className={cn(
        "rounded-[6px] border border-border px-2 py-0.5 font-mono text-[11.5px] leading-[1.3] font-semibold tabular-nums",
        claim.kind === "independent"
          ? "bg-card text-primary"
          : "bg-surface-sunken text-muted-foreground",
      )}
    >
      {claim.number}
    </span>
  );
}

/**
 * A confirmed claim's words, with each named stretch marked and shown with what
 * it stands for.
 *
 * Drawn from the same source the editor offers from, so a claim reads the same
 * whether it is being written in or not, and the numeral is asked for as the
 * words are drawn rather than stored with them.
 */
function Words({ runs }: { runs: ClaimLineResponse["runs"] }) {
  const { references } = useDrawnElements();
  return (
    <>
      {runs.map((run, at) =>
        run.reference === null || run.reference === undefined ? (
          <span key={`${String(at)}:${run.text}`}>{run.text}</span>
        ) : (
          <ReferenceWords
            key={`${String(at)}:${run.text}`}
            reference={run.reference}
            references={references}
          >
            {run.text}
          </ReferenceWords>
        ),
      )}
    </>
  );
}

/** The words as served, for a stage that is read-only. */
function ReadLines({
  claim,
  prefix,
}: {
  claim: ClaimResponse;
  prefix?: string;
}) {
  return (
    <>
      {claim.lines.map((line, at) => (
        <p
          key={`${String(at)}:${line.text}`}
          data-depth={line.depth}
          className={claimLineStyle(claim.kind, line.depth)}
        >
          {line.marker !== null && (
            <span className="mr-1 font-bold text-muted-foreground">
              {line.marker}
            </span>
          )}
          {at === 0 && prefix !== undefined && (
            <span className="font-bold">{`${prefix} `}</span>
          )}
          <Words runs={line.runs} />
        </p>
      ))}
    </>
  );
}

/** What the caption carries beside the kind: a placeholder, or on a read-only stage the yes. */
function ClaimTags({
  taskId,
  claim,
  stage,
  editable,
}: {
  taskId: string;
  claim: ClaimResponse;
  stage: ClaimStrategyResponse;
  editable: boolean;
}) {
  const { tagging } = useClaimSetMoments();
  const category = stage.forms.find((f) => f.form_id === claim.form_id);
  return (
    <>
      {claim.kind === "dependent" ? (
        <InheritedTag claim={claim} stage={stage} />
      ) : editable ? (
        <CategoryTag
          taskId={taskId}
          claim={claim}
          stage={stage}
          pending={tagging.has(claim.claim_id)}
        />
      ) : (
        <StatusPill tone="brand" size="chip" plain>
          {category?.name ?? "No category"}
        </StatusPill>
      )}
      <OriginPill origin={claim.origin} surface="claim" />
      {claim.placeholder && (
        <StatusPill tone="provisional" size="sm">
          Placeholder · awaiting clarification
        </StatusPill>
      )}
      {!editable && claim.confirmed && (
        <StatusPill tone="success" size="sm">
          Confirmed
        </StatusPill>
      )}
    </>
  );
}

/** What the offer writes in place of a first line that opens on a preamble. */
const FURTHER = "further comprising:";

/**
 * A dependent whose words still open on an independent claim's preamble, as
 * one made dependent does. Its words are the attorney's, so they stay as
 * written; this offers the one edit, which runs only on the attorney's click
 * and replaces the first line alone, as any edit of the words does.
 */
function FirstLineOffer({
  claim,
  acts,
}: {
  claim: ClaimResponse;
  acts: ReturnType<typeof useClaimActs>;
}) {
  if (!claim.findings.some((f) => f.kind === "opens_on_a_preamble")) {
    return null;
  }
  return (
    <Button
      variant="secondary"
      size="dense"
      className="mt-2"
      onClick={() => {
        // A refusal is said under the claim by the stage's own refusal line.
        acts
          .saveLines(
            claim.lines.map((line, at) =>
              at === 0
                ? {
                    runs: [{ reference: null, text: FURTHER }],
                    depth: line.depth,
                    numbered: false,
                  }
                : {
                    runs: line.runs,
                    depth: line.depth,
                    numbered: line.numbered,
                  },
            ),
          )
          .catch(() => undefined);
      }}
    >
      Replace the first line with &quot;{FURTHER}&quot;
    </Button>
  );
}

/** The acts at the foot of a claim the attorney may edit: remove it, and give or withdraw the yes. */
function EditActs({
  taskId,
  claim,
  stage,
  acts,
}: {
  taskId: string;
  claim: ClaimResponse;
  stage: ClaimStrategyResponse;
  acts: ReturnType<typeof useClaimActs>;
}) {
  const { markReturned } = useClaimSetMoments();
  return (
    <>
      <RemoveClaim
        taskId={taskId}
        claim={claim}
        stage={stage}
        onReturned={markReturned}
      />
      <ConfirmToggle claim={claim} acts={acts} />
    </>
  );
}

/** A dependent claim with a change Praxis suggests: drawn in its row, outlined as v0.28 outlines it. */
const OUTLINED = "rounded-lg ring-2 ring-primary/60 ring-offset-4";

/**
 * One claim as the design draws it: its number, its kind, its words with their
 * markers, the positions it rests on, and the acts on it. A dependent claim
 * opens with the lead-in the stage serves, composed from the subject and its
 * parent's number, so a renumber or a rename changes no stored word.
 */
export function ClaimRow({
  taskId,
  editable,
  stage,
  claim,
  lead,
  rowRef,
  className,
}: {
  taskId: string;
  /** Whether the stage is open to writes: the thesis record stands. */
  editable: boolean;
  /** The whole stage, which a claim's options read. */
  stage: ClaimStrategyResponse;
  claim: ClaimResponse;
  /** The handle a drag moves the claim by. */
  lead?: ReactNode;
  /** Where a drag attaches, for a row that is itself what moves. */
  rowRef?: (element: Element | null) => void;
  className?: string;
}) {
  const acts = useClaimActs(taskId, claim.claim_id, stage);
  const independent = claim.kind === "independent";
  // An independent claim's change is drawn at the foot of its card, after its dependents.
  const changeOn = useChangesOn(taskId, "claim_strategy");
  const suggestion = independent ? null : changeOn(claim.claim_id);
  const prefix = claim.lead_in ?? undefined;
  const wordsRef = useRef<StructuredTextHandle>(null);
  return (
    <ItemRow
      ref={rowRef}
      id={claimAnchor(claim.claim_id)}
      tabIndex={-1}
      testId={`claim-row-${claim.claim_id}`}
      density={independent ? "roomy" : "dense"}
      className={cn(className, suggestion !== null && OUTLINED)}
      lead={lead}
      marker={<ClaimNumber claim={claim} />}
      label={independent ? "Independent" : "Dependent"}
      tags={<ClaimTags {...{ taskId, claim, stage, editable }} />}
      footerStart={
        <PositionBasis claimId={claim.claim_id} bases={claim.bases} />
      }
      footerEnd={
        editable ? <EditActs {...{ taskId, claim, stage, acts }} /> : undefined
      }
      corner={
        editable ? (
          <ClaimOptions
            taskId={taskId}
            claim={claim}
            stage={stage}
            onEditText={() => wordsRef.current?.focus()}
          />
        ) : undefined
      }
    >
      {editable ? (
        <ClaimLines
          kind={claim.kind}
          lines={claim.lines}
          scheme={stage.marker_scheme}
          leadIn={prefix}
          scope={taskId}
          onSave={acts.saveLines}
          editorRef={wordsRef}
        />
      ) : (
        <ReadLines claim={claim} prefix={prefix} />
      )}
      {/* What the checks raised on the claim when the proposal landed it; none changed a word. */}
      <FindingList
        findings={claim.findings}
        testId="claim-finding"
        className="mt-2"
      />
      {editable && <FirstLineOffer claim={claim} acts={acts} />}
      <ClaimRefusal acts={acts} />
      {suggestion}
    </ItemRow>
  );
}
